import { useQuery } from "@tanstack/react-query";
import { ArrowUp, Square } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import { canAutoFocus } from "@/lib/viewport";
import type { ModelInfo } from "@/lib/types";
import ContextGauge from "./ContextGauge";

const LAST_MODEL = "webthing.lastModel";

export function useModels() {
  return useQuery({ queryKey: ["models"], queryFn: () => api.get<ModelInfo[]>("/api/models"), staleTime: 60_000 });
}

export function rememberModel(id: string) {
  try {
    localStorage.setItem(LAST_MODEL, id);
  } catch {
    /* ignore */
  }
}

/**
 * The model to preselect: the given one (an existing chat's) if usable, else the user's own
 * default, else the one used last in this browser, else the site default, else the first.
 */
export function useDefaultModel(preferred?: string | null): string | null {
  const models = useModels().data;
  if (!models?.length) return null;
  if (preferred && models.some((m) => m.id === preferred)) return preferred;
  const mine = models.find((m) => m.user_default);
  if (mine) return mine.id;
  let last: string | null = null;
  try {
    last = localStorage.getItem(LAST_MODEL);
  } catch {
    /* ignore */
  }
  return models.find((m) => m.id === last)?.id ?? models.find((m) => m.site_default)?.id ?? models[0].id;
}

export function ModelSelect({ value, onChange }: { value: string | null; onChange: (id: string) => void }) {
  const models = useModels();
  const byEndpoint = new Map<string, ModelInfo[]>();
  for (const m of models.data ?? []) {
    if (!byEndpoint.has(m.endpoint)) byEndpoint.set(m.endpoint, []);
    byEndpoint.get(m.endpoint)!.push(m);
  }
  if (models.data && !models.data.length) return <span className="text-sm text-zinc-500">No models available</span>;
  return (
    <select
      value={value ?? ""}
      onChange={(e) => {
        rememberModel(e.target.value);
        onChange(e.target.value);
      }}
      className="max-w-[16rem] truncate rounded-lg bg-transparent py-1 pr-1 text-sm font-medium outline-none hover:bg-zinc-100 dark:hover:bg-zinc-800"
      aria-label="Model"
    >
      {[...byEndpoint].map(([endpoint, list]) =>
        byEndpoint.size > 1 ? (
          <optgroup key={endpoint} label={endpoint}>
            {list.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </optgroup>
        ) : (
          list.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))
        ),
      )}
    </select>
  );
}

export default function Composer({
  onSend,
  onStop,
  streaming,
  disabled,
  placeholder = "Message",
  toolbar,
  attachments,
  canSendEmpty,
  autoFocus = true,
  onPasteFiles,
  context,
  defaultText,
}: {
  onSend: (text: string) => boolean | void | Promise<boolean | void>;
  onStop?: () => void;
  streaming?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Controls shown in the bottom row (model picker, toggles, attach button). */
  toolbar?: ReactNode;
  /** Shown above the text field (pending uploads). */
  attachments?: ReactNode;
  canSendEmpty?: boolean;
  autoFocus?: boolean;
  onPasteFiles?: (files: File[]) => void;
  /** Shows how full the model's context is: tokens used by the conversation so far, and the model's length. */
  context?: { used: number; length: number } | null;
  /** Text to put in the field when it changes (e.g. a template's first message). */
  defaultText?: string;
}) {
  const [text, setText] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (defaultText) setText(defaultText);
  }, [defaultText]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 300)}px`;
  }, [text]);

  async function send() {
    if (disabled || streaming || (!text.trim() && !canSendEmpty)) return;
    const value = text;
    setText("");
    const ok = await onSend(value);
    if (ok === false) setText(value);
    ref.current?.focus();
  }

  return (
    <div className="rounded-2xl border border-zinc-300 bg-white shadow-sm focus-within:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:focus-within:border-zinc-600">
      {attachments}
      <textarea
        ref={ref}
        autoFocus={autoFocus && canAutoFocus()}
        rows={1}
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onPaste={(e) => {
          const files = Array.from(e.clipboardData.files);
          if (files.length && onPasteFiles) {
            e.preventDefault();
            onPasteFiles(files);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send();
          }
        }}
        className="block max-h-[300px] w-full resize-none bg-transparent px-4 pt-3 pb-1 text-[15px] outline-none placeholder:text-zinc-400"
      />
      <div className="flex items-center gap-2 px-2 pb-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">{toolbar}</div>
        {context && <ContextGauge used={context.used} length={context.length} draft={text} />}
        {streaming ? (
          <button
            type="button"
            onClick={onStop}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-900 text-white hover:bg-zinc-700 dark:bg-zinc-100 dark:text-zinc-900"
            aria-label="Stop"
            title="Stop"
          >
            <Square size={12} fill="currentColor" />
          </button>
        ) : (
          <button
            type="button"
            onClick={send}
            disabled={disabled || (!text.trim() && !canSendEmpty)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white hover:bg-indigo-500 disabled:bg-zinc-300 dark:disabled:bg-zinc-700"
            aria-label="Send"
            title="Send"
          >
            <ArrowUp size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
